import { useMemo } from 'react'
import {
  PRMarinaNode,
  usePRFileAllFrames,
  usePRFileSections,
} from '@sketch-hq/sketch-web-renderer'

interface FocusedEntity {
  /** The frame the view is focused on, in a frame detail view. */
  frameUUID?: string
  /** The Section the view is focused on, in a single-Section focus view. */
  sectionUUID?: string
}

/** Whether the node sits anywhere inside the entity with the given UUID. */
const isInsideEntity = (node: PRMarinaNode | null, entityUUID: string) => {
  for (
    let ancestor = node?.getParentNode();
    ancestor;
    ancestor = ancestor.getParentNode()
  ) {
    if (ancestor.getLayerUUID() === entityUUID) {
      return true
    }
  }

  return false
}

/**
 * The UUIDs of every entity an annotation can be anchored to that is currently
 * on the canvas: the frames and Sections of the page being viewed or, in a
 * focus view, only those inside the focused entity.
 *
 * Returns `null` until the page's file has been parsed - what the canvas holds
 * isn't known yet, and nothing is drawn either.
 *
 * `usePRFileAllFrames` reports frames at any nesting depth. Its top-level-only
 * counterpart, `usePRFileArtboards`, leaves out everything inside a Section
 * when the file's root doesn't resolve as a page - which a Preview's
 * presentation file doesn't - and those frames would then be filtered out of
 * the very view that draws their overlay.
 *
 * The presentation file is loaded per page, so this is inherently scoped to the
 * page on screen - an entity of another page is not in the file at all.
 */
export const useVisibleEntityUUIDs = ({
  frameUUID,
  sectionUUID,
}: FocusedEntity = {}) => {
  const fileFrames = usePRFileAllFrames()
  const fileSections = usePRFileSections()

  // The two routes are distinct, so at most one of these is ever set. Either
  // kind of focused entity is isolated by becoming the renderer's scene root,
  // so the canvas holds it and whatever is nested inside it - a frame is no
  // different from a Section here, the frames it contains are drawn with it.
  const focusedUUID = frameUUID ?? sectionUUID

  return useMemo(() => {
    if (!fileFrames || !fileSections) {
      // The focused entity is known from the route even before the file is
      // parsed; what it contains isn't. Report it alone rather than nothing, so
      // an annotation pinned to the entity itself doesn't wait on the file.
      return focusedUUID ? new Set([focusedUUID]) : null
    }

    const entities = [...fileSections, ...fileFrames]

    // Page canvas: everything in the file is on it.
    if (!focusedUUID) {
      return new Set(entities.map(({ objectId }) => objectId))
    }

    const visibleEntityUUIDs = new Set([focusedUUID])

    for (const entity of entities) {
      if (isInsideEntity(entity.getNode(), focusedUUID)) {
        visibleEntityUUIDs.add(entity.objectId)
      }
    }

    return visibleEntityUUIDs
  }, [fileFrames, fileSections, focusedUUID])
}

export default useVisibleEntityUUIDs
